import { z } from 'zod';
import { dueDateSchema } from '../dueDate.js';
export const COMPLETION_RULES = [
    'ALL_RESOURCES',
    'RESOURCES_PLUS_ASSESSMENT',
    'MANUAL',
];
export const RESOURCE_TYPES = [
    'VIDEO', 'PDF', 'PPT', 'DOC', 'EXCEL', 'IMAGE', 'AUDIO', 'ZIP',
    'GOOGLE_DOC', 'GOOGLE_SHEET', 'GOOGLE_SLIDE', 'EXTERNAL_LINK', 'OTHER',
];
/** Types backed by an uploaded file rather than an external URL. */
export const FILE_RESOURCE_TYPES = [
    'VIDEO', 'PDF', 'PPT', 'DOC', 'EXCEL', 'IMAGE', 'AUDIO', 'ZIP', 'OTHER',
];
export const LINK_RESOURCE_TYPES = [
    'GOOGLE_DOC', 'GOOGLE_SHEET', 'GOOGLE_SLIDE', 'EXTERNAL_LINK',
];
export const createCourseSchema = z
    .object({
    title: z.string().trim().min(3, 'Title must be at least 3 characters').max(200),
    description: z.string().trim().max(5000).optional(),
    categoryId: z.string().uuid().optional().nullable(),
    durationMinutes: z.coerce.number().int().min(0).max(100_000).optional().nullable(),
    isMandatory: z.boolean().default(false),
    /** What the material is written in. Shown on the course overview. */
    language: z.string().trim().max(60).optional().nullable(),
    /**
     * The team that owns and maintains this course.
     *
     * Descriptive, not an access rule — who has to take a course is a
     * `CourseAssignment`, and who may browse it is decided by its status.
     */
    ownerDepartmentId: z.string().uuid().optional().nullable(),
    completionRule: z.enum(COMPLETION_RULES).default('ALL_RESOURCES'),
    minAssessmentScore: z.coerce.number().min(0).max(100).optional().nullable(),
    /* Off unless asked for — see the column's note in `schema.prisma`. */
    allowDownload: z.boolean().default(false),
    certificateEnabled: z.boolean().default(false),
    certificateAwardReason: z.string().trim().max(300).optional().nullable(),
    certificateDescription: z.string().trim().max(1200).optional().nullable(),
    tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
})
    .refine((v) => v.completionRule !== 'RESOURCES_PLUS_ASSESSMENT' || v.minAssessmentScore != null, {
    message: 'A minimum assessment score is required for this completion rule',
    path: ['minAssessmentScore'],
});
export const updateCourseSchema = createCourseSchema.innerType().partial();
export const createFolderSchema = z.object({
    name: z.string().trim().min(1, 'Folder name is required').max(120),
    parentFolderId: z.string().uuid().optional().nullable(),
});
export const moveFolderSchema = z.object({
    parentFolderId: z.string().uuid().nullable(),
    sortOrder: z.coerce.number().int().min(0).optional(),
});
/**
 * What "finished this module" means, when a module carries an exam.
 *
 * The author's call: `PASS_ASSESSMENT` for compliance, where the mark is the
 * point, and `ATTEMPT_ASSESSMENT` where sitting it is the point and nobody
 * should ever be permanently stuck behind a module they cannot pass.
 */
export const folderGateRuleSchema = z.enum(['PASS_ASSESSMENT', 'ATTEMPT_ASSESSMENT']);
/**
 * Editing a folder. Moving it is `moveFolderSchema`; they are separate acts.
 *
 * `name` stays required so an existing rename cannot accidentally blank it,
 * and `gateRule` is optional so setting one does not force the caller to
 * re-send the name it did not mean to touch.
 */
export const updateFolderSchema = z.object({
    name: z.string().trim().min(1, 'Folder name is required').max(120),
    gateRule: folderGateRuleSchema.optional(),
});
/**
 * Editing a resource already in a course.
 *
 * Only what can be corrected without re-uploading: its title, its description,
 * and — for a link — where it points. The stored file, its type and its size
 * are what they are; replacing those means adding the resource again, which is
 * honest about what is actually happening.
 */
export const updateResourceSchema = z
    .object({
    title: z.string().trim().min(1, 'Title is required').max(200).optional(),
    description: z.string().trim().max(2000).optional().nullable(),
    externalUrl: z.string().trim().url('Enter a valid URL').max(2000).optional(),
    /**
     * Sent alongside a changed URL, because the link type follows the
     * provider — repointing a Google Doc at a Sheet has to move the icon with
     * it, or the tree lies about what the resource is.
     */
    type: z.enum(LINK_RESOURCE_TYPES).optional(),
})
    .refine((v) => Object.values(v).some((value) => value !== undefined), {
    message: 'Nothing to update',
});
export const createResourceSchema = z
    .object({
    title: z.string().trim().min(1, 'Title is required').max(200),
    description: z.string().trim().max(2000).optional(),
    type: z.enum(RESOURCE_TYPES),
    folderId: z.string().uuid().optional().nullable(),
    /** For uploaded files — the key returned by /uploads/:id/complete. */
    fileKey: z.string().trim().max(500).optional().nullable(),
    mimeType: z.string().trim().max(160).optional().nullable(),
    sizeBytes: z.coerce.number().int().min(0).optional().nullable(),
    durationSeconds: z.coerce.number().int().min(0).optional().nullable(),
    /** For Google Docs/Sheets/Slides and other external links. */
    externalUrl: z.string().trim().url('Enter a valid URL').max(2000).optional().nullable(),
})
    .refine((v) => LINK_RESOURCE_TYPES.includes(v.type)
    ? Boolean(v.externalUrl)
    : Boolean(v.fileKey), {
    message: 'Uploaded resources need a file; link resources need a URL',
    path: ['fileKey'],
});
export const assignCourseSchema = z.object({
    userIds: z.array(z.string().uuid()).min(1, 'Select at least one employee').max(2000),
    dueDate: dueDateSchema,
    isMandatory: z.boolean().default(false),
});
export const resourceProgressSchema = z.object({
    /** Current playhead, for video resume. */
    lastPositionSeconds: z.coerce.number().int().min(0).optional(),
    /** Cumulative watched time, which drives the 90% completion rule. */
    watchedSeconds: z.coerce.number().int().min(0).optional(),
    completed: z.boolean().optional(),
});
export const rateCourseSchema = z.object({
    stars: z.coerce.number().int().min(1).max(5),
});
export const courseListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    search: z.string().trim().max(200).optional(),
    categoryId: z.string().uuid().optional(),
    status: z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']).optional(),
    isMandatory: z.coerce.boolean().optional(),
    /**
     * Where the course came from, not who it is for.
     *
     * `createdById` is one author; `originDepartmentId` is everybody who belongs
     * to one department. Both narrow WITHIN the caller's scope rather than
     * around it — see `originWhere` on the API.
     */
    createdById: z.string().uuid().optional(),
    originDepartmentId: z.string().uuid().optional(),
    sortBy: z.enum(['title', 'createdAt', 'dueDate']).default('createdAt'),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
});
//# sourceMappingURL=course.js.map